<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="12" class="d-flex align-items-center">
        <BreadcrumbMenu/>
      </b-col>
    </b-row>

    <b-card-group deck class="mx-2">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <b-row>
            <b-col cols="6" class="mb-1 mb-lg-0">
              <label class="text-capitalize">
                {{ $tc("pbx_master_account.title", 1) }}
              </label>
              <v-select
                v-model="selectedAccount"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="masterAccountOptions"
                :clearable="false"
                :loading="isLoadingAccounts"
                :disabled="isLoadingAccounts || isMutating"
                data-cy="master-account-selector"
              />
              <small v-if="selectedDomain" class="text-muted">
                {{ selectedDomain }}
              </small>
            </b-col>

            <b-col cols="6" class="d-flex align-items-baseline justify-content-end">
              <b-button
                v-if="$can('update', 'PbxMasterAccount')"
                variant="primary"
                :disabled="!selectedIps.length || isMutating || isLoading"
                data-cy="unban-selected-button"
                class="mt-2 mr-1"
                @click="unblockSelected"
              >
                <b-spinner v-if="isMutating" small class="mr-50"/>
                <span class="text-nowrap text-capitalize">
                  {{ $t("pbx_ip_block.unban_selected") }}
                  <span v-if="selectedIps.length">({{ selectedIps.length }})</span>
                </span>
              </b-button>

              <b-button
                class="btn btn-success"
                @click="fetchBlockedIps()"
              >
                <feather-icon icon="RefreshCwIcon" size="14" />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <b-row>
          <b-col cols="12">
            <div class="ml-2 mb-0">
              <small class="text-muted">IPs bloqueadas {{ tableItems.length }}</small>
            </div>
          </b-col>
        </b-row>
        <b-table
          class="position-relative"
          :items="tableItems"
          :fields="tableColumns"
          :busy="isLoading"
          responsive
          show-empty
          primary-key="ip"
          :empty-text="$t('pbx_ip_block.empty')"
          data-cy="blocked-ip-table"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"/>
              <strong class="ml-1">{{ $t("pbx_ip_block.loading") }}</strong>
            </div>
          </template>

          <template #head(selected)>
            <b-form-checkbox
              v-if="$can('update', 'PbxMasterAccount')"
              v-model="allSelected"
              :indeterminate="selectionIndeterminate"
              :disabled="isMutating || !blockedIps.length"
              data-cy="select-all-ips"
            />
          </template>

          <template #head(ip)="data">
            <span>{{ $t(data.label) }}</span>
          </template>

          <template #head(actions)="data">
            <span>{{ $tc(data.label, 1) }}</span>
          </template>

          <template #cell(selected)="data">
            <b-form-checkbox
              v-if="$can('update', 'PbxMasterAccount')"
              v-model="selectedIps"
              :value="data.item.ip"
              :disabled="isMutating"
              :data-cy="`select-ip-${data.item.ip}`"
            />
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="$can('update', 'PbxMasterAccount')"
              size="sm"
              variant="outline-primary"
              :disabled="isMutating"
              :data-cy="`unban-ip-${data.item.ip}`"
              @click="unblockIp(data.item.ip)"
            >
              <b-spinner
                v-if="isMutating && activeIp === data.item.ip"
                small
                class="mr-50"
              />
              <feather-icon v-else icon="UnlockIcon" size="15"/>
              <span class="ml-50 text-capitalize">
                {{ $t("pbx_ip_block.unban") }}
              </span>
            </b-button>
          </template>
        </b-table>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardGroup,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import {computed, nextTick, ref} from "@vue/composition-api";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {ip, required} from "@validations";
import vSelect from "vue-select";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ability from "@/libs/acl/ability";
import useIpBlockList from "./useIpBlockList";

export default {
  components: {
    BButton,
    BCard,
    BCardGroup,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BSpinner,
    BTable,
    BreadcrumbMenu,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  setup() {
    const refFormObserver = ref(null);
    const list = useIpBlockList();
    const tableItems = computed(() => list.blockedIps.value.map((ipAddress) => ({
      ip: ipAddress,
    })));
    const tableColumns = [{key: "ip", label: "pbx_ip_block.ip"}];
    if (ability.can("update", "PbxMasterAccount")) {
      tableColumns.unshift({key: "selected", label: ""});
      tableColumns.push({key: "actions", label: "actions.name"});
    }

    const getValidationState = ({dirty, validated, valid}) => (
      dirty || validated ? valid : null
    );

    const handleBlock = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      const succeeded = await list.blockIp();
      if (succeeded) {
        await nextTick();
        refFormObserver.value.reset();
      }
    };

    return {
      ...list,
      getValidationState,
      handleBlock,
      refFormObserver,
      tableColumns,
      tableItems,
    };
  },
};
</script>
